import { useQuery } from '@tanstack/react-query';
import { advertisementInput } from '@trafiku/contracts';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { FormActions } from '@/components/form-actions';
import { ImageUpload } from '@/components/image-upload';
import { fc, GeoJsonLayer } from '@/components/map/layers';
import { boundsOf, MapView } from '@/components/map/map-view';
import { ResourceList } from '@/components/resource-list';
import { DailyBars } from '@/components/stats';
import { Combobox } from '@/components/ui/combobox';
import { ColorInput, Field, Input, Select, Switch, Textarea } from '@/components/ui/form';
import { Badge, Card, PageHeader, Spinner } from '@/components/ui/layout';
import { api } from '@/lib/api';
import { useFormState } from '@/lib/form';
import { t } from '@/lib/i18n';
import { useAll, useItem, useSave } from '@/lib/resources';
import { type Advertisement, type Campaign, refId } from '@/lib/types';

export function CampaignSelect({
  value,
  onChange,
  invalid,
}: {
  value: string;
  onChange: (v: string) => void;
  invalid?: boolean;
}) {
  const campaigns = useAll<Campaign>('campaigns', { sort: 'name' });
  return (
    <Combobox
      value={value}
      onChange={onChange}
      invalid={invalid}
      placeholder={t('Search campaign…')}
      options={(campaigns.data?.items ?? []).map((c) => ({
        value: c._id,
        label: c.is_active ? c.name : `${c.name} ${t('(paused)')}`,
      }))}
    />
  );
}

export function AdvertisementsPage() {
  return (
    <ResourceList<Advertisement>
      title={t('Station ads')}
      subtitle={t('Shown on the station screen in the app. Higher priority ads are shown more often.')}
      resource="advertisements"
      basePath="/advertisements"
      newLabel={t('New ad')}
      columns={[
        {
          header: t('Ad'),
          cell: (a) => (
            <div className="flex items-center gap-3">
              {a.photo ? (
                <img src={a.photo} alt="" className="h-10 w-16 rounded object-cover" />
              ) : (
                <div className="h-10 w-16 rounded bg-slate-100" />
              )}
              <span className="font-medium">{a.title}</span>
            </div>
          ),
        },
        {
          header: t('Campaign'),
          cell: (a) => (typeof a.campaign_id === 'object' ? a.campaign_id?.name : '—'),
        },
        { header: t('Priority'), cell: (a) => a.priority },
        { header: t('Views'), cell: (a) => a.views_count.toLocaleString() },
        { header: t('Clicks'), cell: (a) => a.clicks_count.toLocaleString() },
        {
          header: t('Status'),
          cell: (a) => (a.is_active ? <Badge tone="green">{t('Active')}</Badge> : <Badge>{t('Off')}</Badge>),
        },
      ]}
    />
  );
}

interface AdStats {
  by_station: Array<{ station_id: string; name: string; coordinates: [number, number]; count: number }>;
  by_day: Array<{ day: string; count: number }>;
}

function AdStatsCard({ id }: { id: string }) {
  const [type, setType] = useState<'view' | 'click'>('view');
  const stats = useQuery({
    queryKey: ['advertisements', 'stats', id, type],
    queryFn: () => api<AdStats>(`/advertisements/${id}/stats`, { query: { type } }),
  });
  const points = fc(
    (stats.data?.by_station ?? []).map((s) => ({
      type: 'Feature' as const,
      geometry: { type: 'Point' as const, coordinates: s.coordinates },
      properties: { count: s.count, name: s.name },
    })),
  );
  return (
    <Card
      title={t('Statistics')}
      actions={
        <Select className="w-32" value={type} onChange={(e) => setType(e.target.value as 'view' | 'click')}>
          <option value="view">{t('Views')}</option>
          <option value="click">{t('Clicks')}</option>
        </Select>
      }
    >
      {stats.isLoading ? (
        <Spinner />
      ) : (
        <div className="space-y-4">
          <DailyBars data={stats.data?.by_day ?? []} />
          <MapView
            key={type}
            className="h-80"
            fit={boundsOf(stats.data?.by_station.map((s) => s.coordinates))}
          >
            <GeoJsonLayer
              data={points}
              layers={[
                {
                  type: 'heatmap',
                  paint: {
                    'heatmap-weight': ['interpolate', ['linear'], ['get', 'count'], 0, 0, 50, 1],
                    'heatmap-radius': 30,
                    'heatmap-opacity': 0.75,
                  },
                },
              ]}
            />
          </MapView>
          <ol className="divide-y divide-slate-100 text-sm">
            {stats.data?.by_station.slice(0, 10).map((s) => (
              <li key={s.station_id} className="flex justify-between py-1.5">
                <span>{s.name}</span>
                <span className="text-slate-500">{s.count}</span>
              </li>
            ))}
          </ol>
        </div>
      )}
    </Card>
  );
}

export function AdvertisementEditPage() {
  const { id } = useParams();
  const isNew = id === 'new';
  const item = useItem<Advertisement>('advertisements', id);
  const save = useSave<Advertisement>('advertisements', id);
  const navigate = useNavigate();
  const form = useFormState({
    title: '',
    description: '',
    text: '',
    text_color: '#FFFFFF',
    photo: '',
    icon: '',
    link: '',
    ios_link: '',
    android_link: '',
    priority: 1,
    is_active: false,
    campaign_id: '',
  });
  // biome-ignore lint/correctness/useExhaustiveDependencies: load once per item
  useEffect(() => {
    const a = item.data;
    if (a)
      form.setValues({
        title: a.title,
        description: a.description ?? '',
        text: a.text,
        text_color: a.text_color,
        photo: a.photo,
        icon: a.icon,
        link: a.link,
        ios_link: a.ios_link,
        android_link: a.android_link,
        priority: a.priority,
        is_active: a.is_active,
        campaign_id: refId(a.campaign_id),
      });
  }, [item.data]);
  if (!isNew && item.isLoading) return <Spinner />;
  const { values, set, errors } = form;
  return (
    <form
      onSubmit={async (e) => {
        e.preventDefault();
        if (await form.submit(advertisementInput, (d) => save.mutateAsync(d))) navigate('/advertisements');
      }}
    >
      <PageHeader title={isNew ? t('New station ad') : values.title} back="/advertisements" />
      <div className="grid gap-6 xl:grid-cols-[1fr_1fr]">
        <Card>
          <div className="space-y-4">
            <Field label={t('Title')} error={errors.title}>
              {(fid) => (
                <Input
                  id={fid}
                  value={values.title}
                  onChange={(e) => set('title', e.target.value)}
                  invalid={!!errors.title}
                />
              )}
            </Field>
            <Field label={t('Campaign')} error={errors.campaign_id}>
              {() => (
                <CampaignSelect
                  value={values.campaign_id}
                  onChange={(v) => set('campaign_id', v)}
                  invalid={!!errors.campaign_id}
                />
              )}
            </Field>
            <Field label={t('Photo')} hint={t('Ads without a photo are never shown.')}>
              {() => (
                <ImageUpload
                  kind="advertisementPhotos"
                  value={values.photo}
                  onChange={(v) => set('photo', v)}
                />
              )}
            </Field>
            <Field label={t('Logo / icon')}>
              {() => (
                <ImageUpload
                  kind="advertisementIcons"
                  square
                  value={values.icon}
                  onChange={(v) => set('icon', v)}
                />
              )}
            </Field>
            <div className="grid gap-3 sm:grid-cols-[1fr_auto]">
              <Field label={t('Overlay text')}>
                {(fid) => (
                  <Input id={fid} value={values.text} onChange={(e) => set('text', e.target.value)} />
                )}
              </Field>
              <Field label={t('Text colour')} error={errors.text_color}>
                {() => <ColorInput value={values.text_color} onChange={(v) => set('text_color', v)} />}
              </Field>
            </div>
            <Field label={t('Website')} error={errors.link}>
              {(fid) => (
                <Input
                  id={fid}
                  value={values.link}
                  onChange={(e) => set('link', e.target.value)}
                  placeholder="https://"
                  invalid={!!errors.link}
                />
              )}
            </Field>
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label={t('App Store link')} error={errors.ios_link}>
                {(fid) => (
                  <Input
                    id={fid}
                    value={values.ios_link}
                    onChange={(e) => set('ios_link', e.target.value)}
                    invalid={!!errors.ios_link}
                  />
                )}
              </Field>
              <Field label={t('Google Play link')} error={errors.android_link}>
                {(fid) => (
                  <Input
                    id={fid}
                    value={values.android_link}
                    onChange={(e) => set('android_link', e.target.value)}
                    invalid={!!errors.android_link}
                  />
                )}
              </Field>
            </div>
            <Field
              label={t('Priority (1–20)')}
              error={errors.priority}
              hint={t('An ad with priority 4 is shown four times as often as one with 1.')}
            >
              {(fid) => (
                <Input
                  id={fid}
                  type="number"
                  min={1}
                  max={20}
                  value={values.priority}
                  onChange={(e) => set('priority', Number(e.target.value))}
                  className="w-24"
                />
              )}
            </Field>
            <Field label={t('Description')}>
              {(fid) => (
                <Textarea
                  id={fid}
                  value={values.description}
                  onChange={(e) => set('description', e.target.value)}
                />
              )}
            </Field>
            <Switch checked={values.is_active} onChange={(v) => set('is_active', v)} label={t('Active')} />
          </div>
          <div className="mt-6">
            <FormActions saving={save.isPending} resource="advertisements" id={id} backTo="/advertisements" />
          </div>
        </Card>
        {!isNew && id && <AdStatsCard id={id} />}
      </div>
    </form>
  );
}
